import {
    Autocomplete,
    Button,
    Switch,
    TableCell,
    TableRow,
    TextField,
} from "@mui/material";
import {capitalize} from "@mui/material/utils";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useMutation, useQueryClient} from "react-query";
import {useSnackbar} from "notistack";
import {Mandant, Tool} from "../../api";
import {useUpdateTool} from "../../client/hooks";

interface Props {
    tool: Tool;
    mandanten: Mandant[];
}

export default function ToolingRow({tool, mandanten}: Props) {
    const {t: translation} = useTranslation("tooling");
    const {enqueueSnackbar} = useSnackbar();
    const queryClient = useQueryClient();
    const updateTool = useUpdateTool();

    const [isGlobal, setIsGlobal] = useState<boolean>(tool.isGlobal);
    const [mandantIds, setMandantIds] = useState<string[]>(tool.mandantIds ?? []);

    const initialIds = tool.mandantIds ?? [];
    const dirty =
        isGlobal !== tool.isGlobal ||
        mandantIds.length !== initialIds.length ||
        mandantIds.some(id => !initialIds.includes(id));

    const selectedMandanten = mandanten.filter(m => mandantIds.includes(m.id));

    const {mutate: save, isLoading} = useMutation(
        async () => updateTool(tool.id, {isGlobal, mandantIds}),
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(["api", "v1", "tools"]);
                enqueueSnackbar(capitalize(translation("tool-save-successful")), {variant: "success"});
            },
            onError: () => {
                enqueueSnackbar(capitalize(translation("tool-save-failed")), {variant: "error"});
            },
        }
    );

    return (
        <TableRow hover>
            <TableCell>{tool.name}</TableCell>
            <TableCell>{tool.stage}</TableCell>
            <TableCell>
                <Switch
                    checked={isGlobal}
                    onChange={(_, checked) => setIsGlobal(checked)}
                />
            </TableCell>
            <TableCell sx={{minWidth: 280}}>
                <Autocomplete
                    multiple
                    size="small"
                    disabled={isGlobal}
                    options={mandanten}
                    value={selectedMandanten}
                    onChange={(_, value) => {
                        const selectedIds = value.map(m => m.id);
                        const unseenIds = initialIds.filter(id => !mandanten.some(m => m.id === id));
                        setMandantIds([...selectedIds, ...unseenIds]);
                    }}
                    getOptionLabel={(m) => m.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={mandantIds.length === 0 ? translation("tool-mandanten-placeholder") : undefined}
                        />
                    )}
                />
            </TableCell>
            <TableCell align="right">
                <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    disabled={!dirty || isLoading}
                    onClick={() => save()}
                >
                    {capitalize(translation("tool-save"))}
                </Button>
            </TableCell>
        </TableRow>
    );
}
